import { ChangeDetectorRef, Component, Input, OnDestroy } from '@angular/core';
import { MatSnackBarConfig } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { MapBrowserEvent } from 'ol';
import { transform } from 'ol/proj';
import { METERS_PER_UNIT } from 'ol/proj/Units';
import { Subscription, take } from 'rxjs';
import { MapService } from '../../../services/map.service';
import { SnackbarService } from '../../../services/snackbar.service';

@Component({
  selector: 'ebw-info-view',
  templateUrl: './info-view.component.html',
  styleUrls: ['./info-view.component.scss']
})
export class InfoViewComponent implements OnDestroy {
  @Input() decimalPlaces = 6;

  /** Cattura coordinate: il sistema di riferimento e il formato della copia sono fissi (EPSG:4326, "lat,lon") */
  private static readonly CAPTURE_DECIMALS = 6;
  private static readonly CLICK_OWNER_ID = 'capture-coordinates';
  /** Stessa posizione (default Material, in basso) delle altre snackbar della mappa, es. map.service */
  private static readonly SNACKBAR_CONFIG: MatSnackBarConfig = { duration: 3000 };

  isCapturing = false;
  private _captureClickSub: Subscription;

  constructor(
    public mapService: MapService,
    private cdRef: ChangeDetectorRef,
    private _snackbarService: SnackbarService,
    private _translateService: TranslateService
  ) {}

  ngOnDestroy(): void {
    this._stopCapture();
  }

  ngAfterViewChecked() {
    //Doing this to avoid "Expression has changed after it was checked." Error
    this.cdRef.detectChanges();
  }

  DOTS_PER_INCH = 72;
  INCHES_PER_METRE = 39.37;
  UNIT = METERS_PER_UNIT;

  inchesPerUnit(unit: any): number {
    return METERS_PER_UNIT[unit] * this.INCHES_PER_METRE;
  }

  mapRatioScale(resolution): number {
    let scale = resolution * this.inchesPerUnit(this.UNIT) * this.DOTS_PER_INCH;
    return Math.round(scale);
  }

  toggleCapture(): void {
    if (this.isCapturing) {
      this._stopCapture();
      return;
    }
    // Se c'è già un altro strumento il registro mostra l'errore e non concede il click
    const granted = this.mapService.acquireMapClick({
      id: InfoViewComponent.CLICK_OWNER_ID,
      level: 'weak',
      // un tool strong ha preso il click: il registro è già aggiornato, non va rilasciato
      onYield: () => this._stopCapture(false)
    });
    if (granted) {
      this._startCapture();
    }
  }

  private _startCapture(): void {
    this.isCapturing = true;
    this.mapService.customMapClickEnable$.next(true);
    this.mapService.openLayersMap?.getTargetElement()?.classList.add('capture-coordinates-active');
    // Solo il primo click sulla mappa viene catturato
    this._captureClickSub = this.mapService.mapClicked$.pipe(take(1)).subscribe((event) => {
      this._copyCoordinates(event);
      this._stopCapture();
    });
  }

  /** @param releaseRegistry false se il registro è già stato aggiornato (un tool strong ha preso il click) */
  private _stopCapture(releaseRegistry = true): void {
    if (!this.isCapturing) {
      return;
    }
    this.isCapturing = false;
    this._captureClickSub?.unsubscribe();
    // il flag va spento prima di rilasciare il registro: un tool passive in attesa lo riaccende nel suo onResume
    this.mapService.customMapClickEnable$.next(false);
    this.mapService.openLayersMap?.getTargetElement()?.classList.remove('capture-coordinates-active');
    if (releaseRegistry) {
      this.mapService.releaseMapClick(InfoViewComponent.CLICK_OWNER_ID);
    }
  }

  private async _copyCoordinates(event: MapBrowserEvent<any>): Promise<void> {
    const [lon, lat] = transform(event.coordinate, MapService.EPSG_3857_KEY, MapService.EPSG_4326_KEY);
    const text = `${lat.toFixed(InfoViewComponent.CAPTURE_DECIMALS)},${lon.toFixed(InfoViewComponent.CAPTURE_DECIMALS)}`;
    try {
      await navigator.clipboard.writeText(text);
      this._snackbarService.open(this._translateService.instant('map.control_info_view.capture_copied', { coordinates: text }), InfoViewComponent.SNACKBAR_CONFIG);
    } catch {
      this._snackbarService.open(this._translateService.instant('map.control_info_view.capture_error'), InfoViewComponent.SNACKBAR_CONFIG);
    }
  }
}
